import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Autocomplete,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ListAlt as ListAltIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { dataService } from '@services/dataService';
import type { SupplierEvaluation, SupplierTier, SupplierRating, EvaluationDimension } from '../types';

// Configuración de dimensiones con sus criterios por defecto
const dimensionTemplates = {
  quality: {
    name: 'Calidad',
    weight: 30,
    criteria: [
      { name: 'Calidad del producto/servicio', description: 'Cumplimiento de especificaciones técnicas', weight: 40 },
      { name: 'Certificaciones de calidad', description: 'ISO, IRAM u otras certificaciones relevantes', weight: 25 },
      { name: 'Control de calidad', description: 'Procesos de inspección y control', weight: 20 },
      { name: 'Trazabilidad', description: 'Capacidad de rastrear origen y procesos', weight: 15 },
    ],
  },
  reliability: {
    name: 'Confiabilidad',
    weight: 25,
    criteria: [
      { name: 'Cumplimiento de plazos', description: 'Entregas a tiempo según lo acordado', weight: 35 },
      { name: 'Disponibilidad de stock', description: 'Capacidad de respuesta ante pedidos', weight: 25 },
      { name: 'Comunicación', description: 'Claridad y transparencia en la comunicación', weight: 20 },
      { name: 'Solución de problemas', description: 'Respuesta ante reclamos y no conformidades', weight: 20 },
    ],
  },
  competitiveness: {
    name: 'Competitividad',
    weight: 20,
    criteria: [
      { name: 'Precio', description: 'Relación precio/calidad comparado con mercado', weight: 40 },
      { name: 'Condiciones de pago', description: 'Flexibilidad y términos comerciales', weight: 30 },
      { name: 'Descuentos y bonificaciones', description: 'Políticas de descuentos por volumen', weight: 15 },
      { name: 'Garantías', description: 'Cobertura y alcance de garantías', weight: 15 },
    ],
  },
  safety: {
    name: 'Seguridad',
    weight: 15,
    criteria: [
      { name: 'Estándares de seguridad', description: 'Cumplimiento de normas de seguridad laboral', weight: 35 },
      { name: 'Capacitación del personal', description: 'Programas de formación en seguridad', weight: 25 },
      { name: 'Incidentes reportados', description: 'Historial de accidentes e incidentes', weight: 25 },
      { name: 'Equipamiento de protección', description: 'Provisión de EPP y equipos de seguridad', weight: 15 },
    ],
  },
  sustainability: {
    name: 'Sostenibilidad',
    weight: 10,
    criteria: [
      { name: 'Gestión ambiental', description: 'Políticas y certificaciones ambientales', weight: 35 },
      { name: 'Responsabilidad social', description: 'Prácticas de RSE y cumplimiento laboral', weight: 30 },
      { name: 'Eficiencia energética', description: 'Uso de energías renovables y eficiencia', weight: 20 },
      { name: 'Gestión de residuos', description: 'Tratamiento y disposición de residuos', weight: 15 },
    ],
  },
};

// Helper para calcular rating según score
const getRatingFromScore = (score: number): SupplierRating => {
  if (score >= 4.5) return 'strategic';
  if (score >= 3.5) return 'preferred';
  if (score >= 2.5) return 'developing';
  if (score >= 1.5) return 'conditional';
  return 'disabled';
};

// Helper para obtener color según rating
const getRatingColor = (rating: SupplierRating): string => {
  const colors: Record<SupplierRating, string> = {
    strategic: '#2e7d32',
    preferred: '#4caf50',
    developing: '#ff9800',
    conditional: '#ff5722',
    disabled: '#d32f2f',
  };
  return colors[rating];
};

// Helper para obtener label según rating
const getRatingLabel = (rating: SupplierRating): string => {
  const labels: Record<SupplierRating, string> = {
    strategic: 'Estratégico',
    preferred: 'Preferido',
    developing: 'En Desarrollo',
    conditional: 'Condicional',
    disabled: 'Inhabilitado',
  };
  return labels[rating];
};

// ⚠️ WARNINGS IMPORTANTES SOBRE PERSISTENCIA
// ==============================================
// Este módulo utiliza localStorage para persistir las evaluaciones de proveedores.
// Esta es una solución TEMPORAL para demostración y desarrollo.
//
// ANTES DE PRODUCCIÓN se requiere:
// 1. Migrar evaluaciones a SharePoint Lists o base de datos corporativa
// 2. Implementar sistema de aprobaciones con workflows
// 3. Agregar control de versiones de evaluaciones
// 4. Implementar auditoría de cambios
// 5. Agregar permisos por rol (solo ciertas personas pueden evaluar)
// 6. Implementar notificaciones automáticas de vencimiento de evaluaciones
// 7. Agregar firma digital o autenticación para evaluaciones
//
// ⚠️ NO USAR EN PRODUCCIÓN SIN MIGRAR A BACKEND CORPORATIVO
// ==============================================

const STORAGE_KEY = 'interbarge_supplier_evaluations';

const Evaluation: React.FC = () => {
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [tier, setTier] = useState<SupplierTier>(2);
  const [evaluator, setEvaluator] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // Estado de dimensiones
  const [dimensions, setDimensions] = useState<Record<string, EvaluationDimension>>({
    quality: { ...dimensionTemplates.quality, score: 0, criteria: [] },
    reliability: { ...dimensionTemplates.reliability, score: 0, criteria: [] },
    competitiveness: { ...dimensionTemplates.competitiveness, score: 0, criteria: [] },
    safety: { ...dimensionTemplates.safety, score: 0, criteria: [] },
    sustainability: { ...dimensionTemplates.sustainability, score: 0, criteria: [] },
  });
  
  // Estado de evaluaciones guardadas
  const [evaluations, setEvaluations] = useState<SupplierEvaluation[]>([]);
  
  // Estado del modal de detalle
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<SupplierEvaluation | null>(null);
  
  // Acordeón expandido
  const [expanded, setExpanded] = useState<string | false>('quality');
  
  // Estado del tab activo
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    // Cargar proveedores
    const loadedSuppliers = dataService.getSuppliers();
    setSuppliers(loadedSuppliers);
    
    // Cargar evaluaciones desde localStorage
    loadEvaluationsFromStorage();
    
    // Inicializar criterios de dimensiones
    initializeDimensions();
  }, []);

  // Función para cargar evaluaciones desde localStorage
  const loadEvaluationsFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convertir fechas de string a Date objects
        const evaluationsWithDates = parsed.map((ev: any) => ({
          ...ev,
          evaluatedAt: new Date(ev.evaluatedAt),
          validUntil: ev.validUntil ? new Date(ev.validUntil) : undefined,
          dimensions: Object.fromEntries(
            Object.entries(ev.dimensions).map(([key, dim]: [string, any]) => [
              key,
              {
                ...dim,
                criteria: dim.criteria.map((c: any) => ({
                  ...c,
                  evaluatedAt: c.evaluatedAt ? new Date(c.evaluatedAt) : undefined,
                })),
              },
            ])
          ),
        }));
        setEvaluations(evaluationsWithDates);
        console.log(`✅ Loaded ${evaluationsWithDates.length} evaluations from localStorage`);
      }
    } catch (error) {
      console.error('❌ Error loading evaluations from localStorage:', error);
    }
  };

  // Función para guardar evaluaciones en localStorage
  const saveEvaluationsToStorage = (evals: SupplierEvaluation[]) => {
    try {
      const jsonString = JSON.stringify(evals);
      localStorage.setItem(STORAGE_KEY, jsonString);
      console.log(`✅ [Evaluation] Guardado exitoso en localStorage:`, {
        key: STORAGE_KEY,
        totalEvaluaciones: evals.length,
        sizeKB: (jsonString.length / 1024).toFixed(2),
        proveedores: evals.map(e => `${e.supplierName} (${e.rating})`),
      });
      
      // Disparar evento custom para notificar a otros componentes
      window.dispatchEvent(new Event('interbarge-evaluations-updated'));
      
    } catch (error) {
      console.error('❌ [Evaluation] ERROR al guardar evaluaciones:', error);
      alert('⚠️ ERROR: No se pudo guardar la evaluación. Verifica la consola para más detalles.');
    }
  };

  // Función para exportar evaluaciones a JSON (backup)
  const handleExportEvaluations = () => {
    const dataStr = JSON.stringify(evaluations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `evaluaciones-proveedores-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Función para obtener la última evaluación por proveedor
  const getLatestEvaluationsBySupplier = (): Map<string, SupplierEvaluation> => {
    const latestMap = new Map<string, SupplierEvaluation>();
    evaluations.forEach((evaluation) => {
      const existing = latestMap.get(evaluation.supplierId);
      if (!existing || new Date(evaluation.evaluatedAt) > new Date(existing.evaluatedAt)) {
        latestMap.set(evaluation.supplierId, evaluation);
      }
    });
    return latestMap;
  };

  // Handler para cambiar de tab
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const initializeDimensions = () => {
    const initializedDimensions: Record<string, EvaluationDimension> = {};
    
    Object.entries(dimensionTemplates).forEach(([key, template]) => {
      initializedDimensions[key] = {
        name: template.name,
        weight: template.weight,
        score: 0,
        criteria: template.criteria.map((c: { name: string; description: string; weight: number }, idx: number) => ({
          id: `${key}-criterion-${idx}`,
          name: c.name,
          description: c.description,
          score: 0,
          weight: c.weight,
          evidence: '',
          evaluatedAt: undefined,
          evaluatedBy: undefined,
        })),
      };
    });
    
    setDimensions(initializedDimensions);
  };

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleCriterionScoreChange = (dimensionKey: string, criterionId: string, score: number | null) => {
    setDimensions(prev => {
      const dimension = { ...prev[dimensionKey] };
      const criterion = dimension.criteria.find(c => c.id === criterionId);
      if (criterion && score !== null) {
        criterion.score = score;
        criterion.evaluatedAt = new Date();
        criterion.evaluatedBy = evaluator;
        
        // Recalcular score de la dimensión (weighted average de criterios)
        const totalWeight = dimension.criteria.reduce((sum: number, c: any) => sum + c.weight, 0);
        const weightedSum = dimension.criteria.reduce((sum: number, c: any) => sum + (c.score * c.weight), 0);
        dimension.score = totalWeight > 0 ? weightedSum / totalWeight : 0;
      }
      return { ...prev, [dimensionKey]: dimension };
    });
  };

  const handleEvidenceChange = (dimensionKey: string, criterionId: string, evidence: string) => {
    setDimensions(prev => {
      const dimension = { ...prev[dimensionKey] };
      const criterion = dimension.criteria.find((c: any) => c.id === criterionId);
      if (criterion) {
        criterion.evidence = evidence;
      }
      return { ...prev, [dimensionKey]: dimension };
    });
  };

  // Calcular score total (weighted average de dimensiones)
  const calculateTotalScore = (): number => {
    const totalWeight = Object.values(dimensions).reduce((sum, d) => sum + d.weight, 0);
    const weightedSum = Object.values(dimensions).reduce((sum, d) => sum + (d.score * d.weight), 0);
    return totalWeight > 0 ? weightedSum / totalWeight : 0;
  };

  const totalScore = calculateTotalScore();
  const rating = getRatingFromScore(totalScore);

  const handleSaveEvaluation = () => {
    console.log('🔵 [Evaluation] Iniciando guardado de evaluación...');
    
    if (!selectedSupplier) {
      alert('Por favor seleccione un proveedor');
      return;
    }
    
    if (!evaluator.trim()) {
      alert('Por favor ingrese el nombre del evaluador');
      return;
    }
    
    // Validar que todos los criterios tengan score
    const allCriteriaScored = Object.values(dimensions).every(d =>
      d.criteria.every((c: any) => c.score > 0)
    );
    
    if (!allCriteriaScored) {
      alert('Por favor complete todos los criterios de evaluación');
      return;
    }

    const newEvaluation: SupplierEvaluation = {
      id: `eval-${Date.now()}`,
      supplierId: selectedSupplier,
      supplierName: selectedSupplier,
      tier,
      dimensions: {
        quality: dimensions.quality,
        reliability: dimensions.reliability,
        competitiveness: dimensions.competitiveness,
        safety: dimensions.safety,
        sustainability: dimensions.sustainability,
      },
      totalScore,
      rating,
      evaluatedAt: new Date(),
      evaluatedBy: evaluator,
      notes,
    };

    console.log('🔵 [Evaluation] Nueva evaluación creada:', {
      id: newEvaluation.id,
      supplier: newEvaluation.supplierName,
      score: newEvaluation.totalScore,
      rating: newEvaluation.rating,
    });

    const updatedEvaluations = [newEvaluation, ...evaluations];
    console.log(`🔵 [Evaluation] Total evaluaciones después de agregar: ${updatedEvaluations.length}`);
    
    // Actualizar estado local
    setEvaluations(updatedEvaluations);
    
    // Guardar en localStorage
    saveEvaluationsToStorage(updatedEvaluations);
    
    // VERIFICAR que se guardó correctamente
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('✅ [Evaluation] Verificación post-guardado:', {
        totalEnStorage: parsed.length,
        ultimaEvaluacion: parsed[0],
      });
      
      // Cambiar al tab de proveedores evaluados para ver el resultado
      setActiveTab(0);
      
      alert(`✅ Evaluación guardada exitosamente!\n\nProveedor: ${selectedSupplier}\nRating: ${getRatingLabel(rating)} (${totalScore.toFixed(2)})\n\nTotal evaluaciones guardadas: ${parsed.length}\n\n👉 Ahora verás el proveedor en la lista de evaluados.`);
    } else {
      console.error('❌ [Evaluation] ERROR: No se encontró data en localStorage después de guardar!');
      alert('⚠️ ADVERTENCIA: La evaluación pudo no guardarse correctamente. Verifica la consola del navegador.');
    }
    
    // Reset form
    setSelectedSupplier(null);
    setTier(2);
    setEvaluator('');
    setNotes('');
    initializeDimensions();
    setExpanded('quality');
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Evaluación de Proveedores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sistema multidimensional de evaluación con 5 dimensiones ponderadas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportEvaluations}
            disabled={evaluations.length === 0}
          >
            Exportar JSON
          </Button>
          <Chip
            icon={<AssessmentIcon />}
            label={`${evaluations.length} evaluaciones`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab 
            icon={<ListAltIcon />} 
            label={
              <Badge badgeContent={getLatestEvaluationsBySupplier().size} color="primary">
                Proveedores Evaluados
              </Badge>
            } 
            iconPosition="start"
          />
          <Tab icon={<AddIcon />} label="Nueva Evaluación" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Proveedores Evaluados */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon /> Proveedores Evaluados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Lista de proveedores con su rating actual (última evaluación). Estos datos alimentan el Dashboard.
          </Typography>

          {evaluations.length === 0 ? (
            <Alert severity="info">
              No hay proveedores evaluados aún. Ve al tab "Nueva Evaluación" para empezar.
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                ⚠️ Datos en localStorage (temporal). Migrar a SharePoint/Backend antes de producción.
              </Alert>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Proveedor</TableCell>
                      <TableCell align="center">Tier</TableCell>
                      <TableCell align="center">Score Actual</TableCell>
                      <TableCell align="center">Rating</TableCell>
                      <TableCell align="center">Total Evaluaciones</TableCell>
                      <TableCell>Última Evaluación</TableCell>
                      <TableCell>Evaluador</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from(getLatestEvaluationsBySupplier().values())
                      .sort((a, b) => b.totalScore - a.totalScore) // Ordenar por score descendente
                      .map((evaluation) => {
                        const totalEvals = evaluations.filter(e => e.supplierId === evaluation.supplierId).length;
                        return (
                          <TableRow key={evaluation.supplierId} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {evaluation.supplierName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={`Tier ${evaluation.tier}`} size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight="bold">
                                  {evaluation.totalScore.toFixed(2)}
                                </Typography>
                                <Rating value={evaluation.totalScore} readOnly precision={0.1} size="small" />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={getRatingLabel(evaluation.rating)}
                                sx={{
                                  bgcolor: getRatingColor(evaluation.rating),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={totalEvals} size="small" color="default" />
                            </TableCell>
                            <TableCell>
                              {new Date(evaluation.evaluatedAt).toLocaleDateString('es-AR')}
                            </TableCell>
                            <TableCell>{evaluation.evaluatedBy}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedEvaluation(evaluation);
                                  setDetailDialogOpen(true);
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* Tab 1: Nueva Evaluación (Formulario existente) */}
      {activeTab === 1 && (
        <>
          {/* Formulario de Evaluación */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon /> Nueva Evaluación
            </Typography>
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Selector de Proveedor */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedSupplier}
              onChange={(_, newValue) => setSelectedSupplier(newValue)}
              options={suppliers}
              renderInput={(params) => (
                <TextField {...params} label="Proveedor" required />
              )}
            />
          </Grid>

          {/* Tier */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={tier}
                label="Tier"
                onChange={(e) => setTier(e.target.value as SupplierTier)}
              >
                <MenuItem value={1}>Tier 1 - Crítico</MenuItem>
                <MenuItem value={2}>Tier 2 - Importante</MenuItem>
                <MenuItem value={3}>Tier 3 - Apoyo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Evaluador */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Evaluador"
              value={evaluator}
              onChange={(e) => setEvaluator(e.target.value)}
              required
            />
          </Grid>

          {/* Score y Rating - Solo mostrar si hay proveedor seleccionado Y hay score */}
          {selectedSupplier && totalScore > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Score Total
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h3" color="primary.main">
                        {totalScore.toFixed(2)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(totalScore / 5) * 100}
                          sx={{
                            height: 10,
                            borderRadius: 1,
                            bgcolor: 'grey.300',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getRatingColor(rating),
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Rating Asignado
                    </Typography>
                    <Chip
                      label={getRatingLabel(rating)}
                      sx={{
                        bgcolor: getRatingColor(rating),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        px: 2,
                        py: 3,
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Dimensiones de Evaluación */}
        {selectedSupplier && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Dimensiones de Evaluación
            </Typography>
            
            {Object.entries(dimensions).map(([key, dimension]) => (
              <Accordion
                key={key}
                expanded={expanded === key}
                onChange={handleAccordionChange(key)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                    <Typography sx={{ flex: 1, fontWeight: 'bold' }}>
                      {dimension.name}
                    </Typography>
                    <Chip
                      label={`${dimension.weight}%`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Score:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">
                        {dimension.score.toFixed(2)}
                      </Typography>
                      <Rating value={dimension.score} readOnly precision={0.1} size="small" />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="25%">Criterio</TableCell>
                          <TableCell width="10%" align="center">Peso</TableCell>
                          <TableCell width="20%" align="center">Puntuación (1-5)</TableCell>
                          <TableCell width="45%">Evidencia / Observaciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dimension.criteria.map((criterion: any) => (
                          <TableRow key={criterion.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {criterion.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {criterion.description}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={`${criterion.weight}%`} size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Rating
                                value={criterion.score}
                                onChange={(_, newValue) =>
                                  handleCriterionScoreChange(key, criterion.id, newValue)
                                }
                                precision={1}
                                max={5}
                                size="large"
                                icon={<StarIcon fontSize="inherit" />}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                placeholder="Evidencia, notas o comentarios..."
                                value={criterion.evidence || ''}
                                onChange={(e) =>
                                  handleEvidenceChange(key, criterion.id, e.target.value)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Notas Generales */}
        {selectedSupplier && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notas Generales"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Comentarios adicionales sobre la evaluación..."
            />
          </Box>
        )}

        {/* Botón Guardar */}
        {selectedSupplier && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveEvaluation}
            >
              Guardar Evaluación
            </Button>
          </Box>
        )}
      </Paper>

      {/* InfoCard */}
      {!selectedSupplier && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Seleccione un proveedor para comenzar la evaluación. El sistema calculará automáticamente el score total y asignará un rating según los criterios evaluados.
        </Alert>
      )}
        </>
      )}

      {/* Modal de Detalle de Evaluación */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detalle de Evaluación - {selectedEvaluation?.supplierName}
          </Typography>
          <IconButton onClick={() => setDetailDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvaluation && (
            <Box>
              {/* Header con Score y Rating */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Score Total
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h3" color="primary.main">
                        {selectedEvaluation.totalScore.toFixed(2)}
                      </Typography>
                      <Rating value={selectedEvaluation.totalScore} readOnly precision={0.1} size="large" />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Rating
                    </Typography>
                    <Chip
                      label={getRatingLabel(selectedEvaluation.rating)}
                      sx={{
                        bgcolor: getRatingColor(selectedEvaluation.rating),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        px: 3,
                        py: 3,
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Metadata */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Tier</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    Tier {selectedEvaluation.tier}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Evaluador</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedEvaluation.evaluatedBy}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Fecha</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(selectedEvaluation.evaluatedAt).toLocaleDateString('es-AR')}
                  </Typography>
                </Grid>
              </Grid>

              {/* Dimensiones */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Dimensiones Evaluadas
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dimensión</TableCell>
                      <TableCell align="center">Peso</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell align="center">Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selectedEvaluation.dimensions).map(([key, dimension]: [string, any]) => (
                      <TableRow key={key}>
                        <TableCell>{dimension.name}</TableCell>
                        <TableCell align="center">
                          <Chip label={`${dimension.weight}%`} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {dimension.score.toFixed(2)}
                            </Typography>
                            <Rating value={dimension.score} readOnly precision={0.1} size="small" />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <LinearProgress
                            variant="determinate"
                            value={(dimension.score / 5) * 100}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'grey.300',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Notas Generales */}
              {selectedEvaluation.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Notas Generales
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2">
                      {selectedEvaluation.notes}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Evaluation;
